import classNames from "classnames";
import * as moment from "moment";
import React from "react";
import { Popup } from "semantic-ui-react";

import { Modal } from "../../components/";
import { NoDataAvailableDiv, NoDataAvailableWarning } from "../../components/NoDataAvailableDiv";
import { Loading, Panel } from "../../components/panels/index";

import * as action from "../../redux/actionCreators/guestlist";
import { selector } from "../index";
import { GuestAccordion, GuestAdd, GuestlistSettings, GuestSearch } from "./";
import { guestlistIsEmpty } from "./helpers";

import { ButtonSecondary } from "components/Atoms/Button";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { useDispatch, useSelector } from "react-redux";
import { formatLongDateWithTime } from "utilities";

interface Props {
  config: server.dto.GuestlistPanelConfig
}

const Guestlist = (props: Props) => {
  const dispatch = useDispatch();

  const showGuestAddModal = (guest?: server.dto.LegacyGuest) => dispatch(action.showAddGuestModal(guest));
  const showGuestlistSettings = () => dispatch(action.showGuestlistSettings());

  const {
    canEditGuestlistSettings,
    guestlist,
    hasData,
    hasError,
    isLoading,
    isSearching,
    searchResult,
    shouldShowGuestAddModal,
    shouldShowGuestlistSettings,
    showId,
    guestCount
  } = useSelector(state => ({
    canEditGuestlistSettings: selector.canEditGuestlistSettings(state),
    guestlist: selector.getGuestlist(state),
    hasData: selector.getHasData(state),
    hasError: selector.getHasError(state),
    isLoading: selector.getIsLoading(state),
    isSearching: selector.getIsSearching(state),
    searchResult: selector.getSearchResult(state),
    shouldShowGuestAddModal: selector.getAddingModalIsShown(state),
    shouldShowGuestlistSettings: selector.getGuestlistSettingsModalIsShown(state),
    showId: selector.getShowId(state),
    guestCount: selector.getGuestCount(state)
  }));

  const isClosed = () => moment(guestlist.deadlineUtc) < moment();
  const closed = (): string => isClosed() ? RESX.Guestlist.ClosedStatus : RESX.Guestlist.OpenStatus;
  const getCheckIconName = (isCheck) => isCheck ? "icon-true" : "icon-false";
  const hiddenIfTrue = (valueToCheck) => valueToCheck ? "hidden" : "visible";

  const onSearch = (keyword) => {
    dispatch(action.setSearchQuery(keyword));
  };

  const handleClosePlacesModal = () => {
    dispatch(action.hideGuestlistSettings());
    dispatch(action.fetchGuestlist(guestlist.showId));
  };

  const handleCloseGuestAddModal = () => {
    dispatch(action.hideAddGuestModal());
    dispatch(action.fetchGuestlist(guestlist.showId));
  };

  const onGuestlistCreateClick = () => {
    if (isLoading) return;
    dispatch(action.addGuestlist(showId))
      .then(() => dispatch(action.fetchGuestlist(showId)))
      .then(() => dispatch(action.showGuestlistSettings()));
  };

  const deleteGuestlist = () => {
    dispatch(action.removeGuestlist(guestlist.guestlistId))
      .then(() => dispatch(action.fetchGuestlist(showId)));
  };

  const canAddGuest = !isLoading && hasData && !hasError && guestlist?.availablePlaces.length !== 0 && props.config.canAdd;
  const canCreateGuestlist = !isLoading && !hasError && !hasData && props.config.canAdd;

  let onAddClicked: () => void;
  if (canAddGuest) {
    onAddClicked = showGuestAddModal;
  } else if (canCreateGuestlist) {
    onAddClicked = onGuestlistCreateClick;
  }

  const { refToScrollTo } = useShortcutPanel({
    label: props.config.headerLabel,
    totalItems: guestCount,
    onAddClicked,
    type: "guestlist"
  });

  if (isLoading && !hasData) {
    return (<Panel
      panelClassName="s1_related-feature-panel"
      panelRef={refToScrollTo}
      title={props.config.headerLabel}>
      <Loading />
    </Panel>);
  }

  if (hasError) {
    return (<Panel
      panelClassName="s1_related-feature-panel"
      panelRef={refToScrollTo}
      hasError={true}
      title={props.config.headerLabel}>
    </Panel>);
  }

  if (!hasData) {
    const activateBtn = canEditGuestlistSettings
      ? <ButtonSecondary onClick={onGuestlistCreateClick} label={RESX.Buttons.btnActivate} />
      : null;

    return <Panel
      panelClassName="s1_related-feature-panel"
      title={props.config.headerLabel}
      button={activateBtn}
      panelRef={refToScrollTo}>
      <NoDataAvailableDiv>{RESX.Guestlist.NoExistingGuestlist}</NoDataAvailableDiv>
    </Panel>;
  }

  // Either show the "add guest" button or show a popup saying it's not possible because there are no types set up
  const guestAddButton = guestlist.availablePlaces.length === 0
    ? <Popup trigger={<ButtonSecondary disabled={true} label={RESX.Guestlist.AddGuestBtn} />}
      content={RESX.Guestlist.NoTypesExistTooltip} hideOnScroll={true} on="hover" wide />
    : <button className="button-new secondary" type="button" disabled={guestlist.availablePlaces.length === 0} onClick={() => {
      showGuestAddModal();
    }}>{
        RESX.Guestlist.AddGuestBtn}</button>;

  const downloadClass = classNames("icon-download", { disabled: guestlistIsEmpty(guestlist) });

  return (
    <Panel
      panelClassName="s1_related-feature-panel"
      panelRef={refToScrollTo}
      title={props.config.headerLabel}
      button={guestAddButton}>
      <Modal isOpen={shouldShowGuestlistSettings}
        handleClose={handleClosePlacesModal}
        title={RESX.GeneralLabels.Settings}
        width="large"
        height={500}
      >
        <GuestlistSettings onDelete={deleteGuestlist} />
      </Modal>
      <Modal isOpen={shouldShowGuestAddModal}
        handleClose={handleCloseGuestAddModal}
        title={RESX.Guestlist.Guest}
        width="small"
      >
        <GuestAdd onCloseModal={handleCloseGuestAddModal} />
      </Modal>

      <div className="content-section margin-bottom flex gap">
        <div className="grow1">
          <table>
            <tbody>
              <tr>
                <td style={{ paddingRight: 10 }}>{RESX.Status.resxStatus}</td>
                <td>{closed()}</td>
              </tr>
              <tr>
                <td style={{ paddingRight: 10 }}>{RESX.Guestlist.ClosingDate}</td>
                <td>{formatLongDateWithTime(guestlist.deadlineUtc)}</td>
              </tr>
              <tr>
                <td style={{ paddingRight: 10 }}>{RESX.Guestlist.EmailMandatoryLbl}</td>
                <td><i className={getCheckIconName(guestlist.emailMandatory)}></i></td>
              </tr>
              <tr>
                <td style={{ paddingRight: 10 }}>{RESX.Guestlist.PhoneMandatoryLbl}</td>
                <td><i className={getCheckIconName(guestlist.phoneMandatory)}></i></td>
              </tr>
              <tr>
                <td style={{ paddingRight: 10 }}>{RESX.Guestlist.Passport}</td>
                <td><i className={getCheckIconName(guestlist.passportRequired)}></i></td>
              </tr>
              <tr>
                <td style={{ paddingRight: 10 }}>{RESX.Guestlist.MultiplePerNameLbl}</td>
                <td><i className={getCheckIconName(guestlist.multipleGuestsPerName)}></i></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div>
          <a className={downloadClass} style={{ visibility: hiddenIfTrue(isLoading) }} href={`${site.coreApiBaseUrl}/guestlist/export/xlsx/${guestlist.showId}`} title={RESX.Template.Export}></a>
        </div>

        {canEditGuestlistSettings &&
          <div>
            <a className="icon-cog" title={RESX.GeneralLabels.Settings} style={{ visibility: hiddenIfTrue(isLoading) }} onClick={() => {
              showGuestlistSettings();
            }}></a>
          </div>
        }

      </div>
      <div className="flex margin-bottom" style={{ width: "100%" }}>
        {!guestlistIsEmpty(guestlist) && <GuestSearch onSearch={onSearch} />}
      </div>

      {guestlist.availablePlaces.length === 0
        ? <NoDataAvailableWarning>{RESX.Guestlist.NoTypesExist}</NoDataAvailableWarning>
        : <GuestAccordion places={isSearching ? searchResult : guestlist.availablePlaces} isSearching={isSearching} />
      }
    </Panel>
  );
};

export default Guestlist;